<template>
  <div v-if="isEnabled" class="chat">
    <div v-show="isChatOpen" class="chat--wrapper">
      <LoadingSpinner
        v-if="isLoading"
        class="content--loading-spinner"
        :dimensions="{ width: '40', height: '40' }"
      />

      <iframe
        v-if="isInitiated"
        ref="twilio-widget"
        class="content--twilio-chat"
        src="https://production-twilio-flexv4.live-kfz42.de/index.html?v=2"
        frameborder="0"
        @load="loadCompleted()"
      ></iframe>
      <!-- Staging https://instance0-staging-twilio-flex.live-kfz42.de/index.html -->
    </div>

    <div class="chat--toggle-btn" @click="toggleChat()">
      <ChatServiceSvg v-show="!isChatOpen" width="28" height="28" />
      <ChatArrowDownSvg v-show="isChatOpen" width="28" height="28" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import LocalStorage from 'Libs/helpers/local-storage';
import ChatServiceSvg from 'Components/00-generated/ChatServiceSvg';
import ChatArrowDownSvg from 'Components/00-generated/ChatArrowDownSvg';
import LoadingSpinner from 'Components/01-atoms/loading-spinner/LoadingSpinner';

export default {
  name: 'TwilioChat',
  components: {
    ChatServiceSvg,
    ChatArrowDownSvg,
    LoadingSpinner,
  },
  data() {
    return {
      isChatOpen: false,
      isLoading: false,
      isInitiated: false, // Chat is not loaded unless opened
      localStorage: new LocalStorage(),
    };
  },
  computed: {
    ...mapState('core', {
      isEnabled: ({ salesChannel: { salesChannelId } }) =>
        ['www-kfzteile-com', 'www-autoteile24-de'].includes(salesChannelId),
    }),
  },
  mounted() {
    if (!this.isEnabled) return;

    this.$nextTick(() => {
      this.loadChat();
    });
  },
  destroyed() {
    if (!this.isInitiated) return;

    window.removeEventListener('message', () => {});
  },
  methods: {
    loadChat() {
      // Keep Chat open if the user didn't close it
      if (
        this.localStorage?.getItem('isChatOpen') === 'true' &&
        !this.isChatOpen
      ) {
        this.toggleChat();
      } else {
        this.autoPopup();
      }
    },
    loadCompleted() {
      window.addEventListener('message', (e) => {
        const twilioIframe = this.$refs['twilio-widget'];
        const message = e?.data?.event;

        if (!message || !twilioIframe) return;

        if (message === 'chat-loaded') {
          // Send a postMessage to make sure the chat open as well on Twilio side
          twilioIframe?.contentWindow?.postMessage('open-chat', '*');
          this.isLoading = false;
        }

        if (message === 'hide-chat' && this.isChatOpen) {
          this.toggleChat();
        }
      });
    },
    toggleChat(isOpenedByAutoPopup = false) {
      if (!this.isChatOpen && !this.isInitiated) {
        this.isLoading = true;
        this.isInitiated = true;
      }

      this.isChatOpen = !this.isChatOpen;

      this.localStorage.setItem('isAutoPopupDone', 'true');

      // Shouldn't stay open if not opened by the user
      if (!isOpenedByAutoPopup) {
        this.localStorage.setItem('isChatOpen', this.isChatOpen);
      }
    },
    autoPopup() {
      // 30s Chat Auto-pop-up
      if (this.localStorage?.getItem('isAutoPopupDone') === 'true') return;

      setTimeout(() => {
        if (
          this.isChatOpen ||
          this.localStorage?.getItem('isAutoPopupDone') === 'true'
        ) {
          return;
        }

        this.toggleChat(true);
      }, 30000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'mixins';
@import 'variables/breakpoints';

.chat {
  position: fixed;
  right: var(--space-3);
  bottom: var(--space-2);
  z-index: 9997;

  &--wrapper {
    position: relative;
    overflow: hidden;
    width: 330px;
    height: 450px;
    border-radius: var(--space-1--half);
    box-shadow: rgb(0 0 0 / 40%) 0 2px 4px 0;
    margin-bottom: var(--space-2--half);
    background-color: var(--color-white);

    .content {
      &--loading-spinner {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
        background-color: var(--color-white);
      }

      &--twilio-chat {
        width: 100%;
        height: 100%;
      }
    }
  }

  &--toggle-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 60px;
    height: 60px;
    margin-left: auto;
    cursor: pointer;
    user-select: none;
    border-radius: 50%;
    background-color: var(--color-primary);
    box-shadow: rgb(0 0 0 / 50%) 0 2px 4px 0;
    transition: ease background-color 0.3s;

    .chatArrowDownSvg {
      padding-top: var(--space-0--half);
    }

    &:hover {
      background-color: var(--color-secondary);
    }
  }
}
</style>
